// useDispatchStream — phone-side live view of a remote dispatch.
//
// Opens the SSE viewer (POST /api/dispatch/:id/stream) which replays the
// dispatch_events log then tails new events. Accumulates events, tracks
// terminal status, and derives the current pending approval (the latest
// approval_request without a matching approval_decision).

import { useEffect, useState } from 'react';
import { streamSSE } from '../lib/sseClient.js';

function derivePendingApproval(events) {
  const decided = new Set();
  for (const e of events) {
    if (e?.type === 'approval_decision' && e.payload?.request_id) decided.add(e.payload.request_id);
  }
  let pending = null;
  for (const e of events) {
    if (e?.type === 'approval_request' && e.payload?.request_id && !decided.has(e.payload.request_id)) {
      pending = { request_id: e.payload.request_id, desc: e.payload.desc };
    }
  }
  return pending;
}

export function useDispatchStream(id) {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setStatus('idle');
      setEvents([]);
      return undefined;
    }
    let active = true;
    const controller = new AbortController();
    setStatus('connecting');
    setEvents([]);
    setError(null);

    streamSSE(`/api/dispatch/${id}/stream`, {
      body: {},
      signal: controller.signal,
      onChunk: (evt) => {
        if (!active || !evt) return;
        if (evt.type === 'end') {
          setStatus(evt.status || 'ended');
          return;
        }
        setEvents((prev) => [...prev, evt]);
        setStatus(evt.type === 'complete' ? 'completed' : 'streaming');
      },
    })
      .then((res) => {
        if (!active) return;
        if (res && !res.ok && res.error !== 'aborted') setError(res.error || 'stream error');
      })
      .catch(() => {});

    return () => {
      active = false;
      controller.abort();
    };
  }, [id]);

  return { events, status, error, pendingApproval: derivePendingApproval(events) };
}
